import type { AppDispatch } from "@/providers/store/store";

import customLayersApi from "./custom-layers";
import {
  CustomLayerOriginType,
  CustomLayerTypeFilter,
  type ICustomLayer,
  type ICustomLayerListResponse,
  type IGetCustomLayersParams,
} from "./custom-layers.interface";

function listQueryMatchesLayer(args: IGetCustomLayersParams, layer: ICustomLayer): boolean {
  if (args.targetType != null && args.targetType !== layer.targetType) {
    return false;
  }

  if (args.schoolLevelType != null && args.schoolLevelType !== layer.type) {
    return false;
  }

  const typeFilter = args.type ?? CustomLayerTypeFilter.CUSTOM;
  if (typeFilter === CustomLayerTypeFilter.ALL) {
    return true;
  }

  const expectedOrigin =
    typeFilter === CustomLayerTypeFilter.IMPORTED
      ? CustomLayerOriginType.IMPORTED
      : CustomLayerOriginType.CUSTOM;

  return layer.layerType === expectedOrigin;
}

function patchAllCustomLayerListQueries(
  dispatch: AppDispatch,
  getState: () => unknown,
  districtId: string,
  shouldPatch: (args: IGetCustomLayersParams) => boolean,
  patchFn: (draft: ICustomLayerListResponse) => void,
): void {
  const state = getState() as { customLayersApi?: { queries?: Record<string, {
    endpointName?: string;
    originalArgs?: IGetCustomLayersParams;
  } | undefined> } };
  const queries = state.customLayersApi?.queries ?? {};

  for (const entry of Object.values(queries)) {
    if (entry?.endpointName !== "getCustomLayersByDistrict") continue;

    const { originalArgs } = entry;
    if (originalArgs?.districtId !== districtId) continue;
    if (!shouldPatch(originalArgs)) continue;

    dispatch(
      customLayersApi.util.updateQueryData(
        "getCustomLayersByDistrict",
        originalArgs,
        patchFn,
      ),
    );
  }
}

export function upsertCustomLayerInListCache(
  dispatch: AppDispatch,
  getState: () => unknown,
  districtId: string,
  layer: ICustomLayer,
): void {
  patchAllCustomLayerListQueries(
    dispatch,
    getState,
    districtId,
    (args) => listQueryMatchesLayer(args, layer),
    (draft) => {
      if (draft.items.some((item) => item.id === layer.id)) return;

      draft.items.unshift(layer);
      draft.pagination.total += 1;

      const limit = draft.pagination.limit > 0 ? draft.pagination.limit : 50;
      draft.pagination.totalPages = Math.ceil(draft.pagination.total / limit);
      draft.pagination.hasMore = draft.pagination.page < draft.pagination.totalPages;
    },
  );
}
